/**
 * Game time manager
 * Supports both normal mode (using real-time) and replay mode (using simulated time)
 */
class GameTimeManager {
  private replayMode: boolean = true;
  private simulatedTimestamp: number = 1629634800000;
  private playbackSpeed: number = 5.0; // 1.0 = real-time, 2.0 = 2x speed, etc. Negative for reverse
  private autoAdvanceInterval: ReturnType<typeof setInterval> | null = null;
  private lastRealTime: number = Date.now();
  private lastLogTime: number = 0;
  private initialTimestamp: number | null = null; // Store the initial timestamp for reset
  private resetCheckInterval: ReturnType<typeof setInterval> | null = null; // Interval to check for reset
  private readonly RESET_INTERVAL_MS = 60 * 60 * 1000; // 30 minutes in milliseconds
  private readonly MIN_TIMESTAMP = 1629634800000; // Minimum timestamp (starting point)
  private isPaused: boolean = false; // Pause state

  /**
   * Set replay mode with optional starting timestamp
   */
  public setReplayMode(enabled: boolean, startTimestamp?: number): void {
    this.replayMode = enabled;
    if (enabled && startTimestamp !== undefined) {
      this.simulatedTimestamp = startTimestamp;
      this.initialTimestamp = startTimestamp; // Save initial timestamp for reset
      this.lastRealTime = Date.now();
    }
    if (enabled && startTimestamp === undefined) {
      // entering replay without explicit start: anchor now
      this.lastRealTime = Date.now();
      // If we don't have an initial timestamp yet, use current simulated timestamp
      if (this.initialTimestamp === null) {
        this.initialTimestamp = this.simulatedTimestamp;
      }
    }
    // Stop auto-advance when disabling replay mode
    if (!enabled) {
      this.stopAutoAdvance();
      this.stopResetCheck();
    } else {
      // Start reset check when enabling replay mode
      this.startResetCheck();
    }
  }

  /**
   * Set the simulated timestamp (for replay mode)
   */
  public setSimulatedTimestamp(timestamp: number): void {
    if (this.replayMode) {
      this.simulatedTimestamp = timestamp;
      this.lastRealTime = Date.now();
    }
  }

  /**
   * Get current simulated timestamp
   */
  public getSimulatedTimestamp(): number {
    if (this.autoAdvanceInterval && !this.isPaused) {
      // Auto-advance is running, calculate current time
      const now = Date.now();
      const elapsed = now - this.lastRealTime;
      let currentTime = this.simulatedTimestamp + elapsed * this.playbackSpeed;
      
      // If reversing, clamp to minimum timestamp
      if (this.playbackSpeed < 0 && currentTime < this.MIN_TIMESTAMP) {
        currentTime = this.MIN_TIMESTAMP;
        this.setSimulatedTimestamp(this.MIN_TIMESTAMP);
        this.playbackSpeed = Math.abs(this.playbackSpeed); // Switch to forward
      }
      
      // Log every 1000ms to avoid spam
      if (Math.floor(currentTime / 1000) !== Math.floor(this.lastLogTime / 1000)) {
        this.lastLogTime = currentTime;
        // console.log('[TimeUtils] Simulated time:', new Date(currentTime).toISOString(), 
        //   'speed:', this.playbackSpeed, 'elapsed:', elapsed, 'ms');
      }
      return currentTime;
    }
    return this.simulatedTimestamp;
  }

  /**
   * Set playback speed (positive = forward, negative = reverse, 0 = paused)
   * Minimum speed is 5.0x
   */
  public setPlaybackSpeed(speed: number): void {
    if (speed === 0) {
      this.isPaused = true;
      return;
    }
    this.isPaused = false;
    // Enforce minimum speed of 5.0x
    if (speed > 0 && speed < 5.0) {
      speed = 5.0;
    }
    if (speed < 0 && Math.abs(speed) < 5.0) {
      speed = -5.0;
    }
    this.playbackSpeed = speed;
  }

  /**
   * Get current playback speed
   */
  public getPlaybackSpeed(): number {
    return this.playbackSpeed;
  }

  public isReplayMode(): boolean {
    return this.replayMode;
  }

  /**
   * Start auto-advancing simulated time
   * @param intervalMs - Update interval in milliseconds (default: 100ms)
   */
  public startAutoAdvance(intervalMs: number = 100): void {
    if (!this.replayMode) {
      console.warn('[TimeUtils] Cannot start auto-advance in normal mode');
      return;
    }

    if (this.autoAdvanceInterval) {
      // Already running, just update the interval
      this.stopAutoAdvance();
    }

    // Ensure initial timestamp is set if not already set
    if (this.initialTimestamp === null) {
      this.initialTimestamp = this.simulatedTimestamp;
    }

    console.log('[TimeUtils] Starting auto-advance, interval:', intervalMs, 'ms, speed:', this.playbackSpeed, 
      'start time:', new Date(this.simulatedTimestamp).toISOString());
    this.lastRealTime = Date.now();
    this.autoAdvanceInterval = setInterval(() => {
      if (!this.isPaused) {
        const now = Date.now();
        const elapsed = now - this.lastRealTime;
        this.simulatedTimestamp += elapsed * this.playbackSpeed;
        
        // If reversing, clamp to minimum timestamp
        if (this.playbackSpeed < 0 && this.simulatedTimestamp < this.MIN_TIMESTAMP) {
          this.simulatedTimestamp = this.MIN_TIMESTAMP;
          this.playbackSpeed = Math.abs(this.playbackSpeed); // Switch to forward
        }
        
        this.lastRealTime = now;
      }
    }, intervalMs);
    
    // Start reset check when auto-advance starts
    this.startResetCheck();
  }

  /**
   * Stop auto-advancing simulated time
   */
  public stopAutoAdvance(): void {
    if (this.autoAdvanceInterval) {
      clearInterval(this.autoAdvanceInterval);
      this.autoAdvanceInterval = null;
    }
    // Also stop reset check when auto-advance stops
    this.stopResetCheck();
  }

  /**
   * Check if auto-advance is running
   */
  public isAutoAdvanceRunning(): boolean {
    return this.autoAdvanceInterval !== null;
  }

  /**
   * Get the simulated timestamp for a specific block
   * This would need to be implemented to map block numbers to timestamps
   */
  public getTimestampForBlock(blockNumber: number): Promise<number> {
    // This would need to query the blockchain for the block timestamp
    // For now, return current simulated time
    return Promise.resolve(this.simulatedTimestamp);
  }

  /**
   * Reset time to initial timestamp
   */
  private resetToInitialTime(): void {
    if (this.initialTimestamp !== null) {
      console.log('[TimeUtils] Resetting time to initial timestamp:', 
        new Date(this.initialTimestamp).toISOString());
      this.setSimulatedTimestamp(this.initialTimestamp);
      this.lastRealTime = Date.now();
    }
  }

  /**
   * Start checking for time reset (every second)
   */
  private startResetCheck(): void {
    if (this.resetCheckInterval) {
      return; // Already running
    }
    
    this.resetCheckInterval = setInterval(() => {
      if (!this.replayMode || !this.autoAdvanceInterval || this.initialTimestamp === null) {
        return;
      }

      const currentTime = this.getSimulatedTimestamp();
      const elapsed = currentTime - this.initialTimestamp;
      
      // Check if 10 minutes (in game time) have passed
      if (elapsed >= this.RESET_INTERVAL_MS) {
        console.log('[TimeUtils] 30 minutes elapsed, resetting time to initial state');
        this.resetToInitialTime();
      }
    }, 1000); // Check every second
  }

  /**
   * Stop checking for time reset
   */
  private stopResetCheck(): void {
    if (this.resetCheckInterval) {
      clearInterval(this.resetCheckInterval);
      this.resetCheckInterval = null;
    }
  }

  /**
   * Get initial timestamp
   */
  public getInitialTimestamp(): number | null {
    return this.initialTimestamp;
  }

  /**
   * Toggle playback direction (forward/reverse)
   */
  public toggleDirection(): void {
    this.playbackSpeed = -this.playbackSpeed;
    // Ensure minimum speed
    if (this.playbackSpeed > 0 && this.playbackSpeed < 5.0) {
      this.playbackSpeed = 5.0;
    }
    if (this.playbackSpeed < 0 && Math.abs(this.playbackSpeed) < 5.0) {
      this.playbackSpeed = -5.0;
    }
    this.isPaused = false;
  }

  /**
   * Check if time is paused
   */
  public getIsPaused(): boolean {
    return this.isPaused;
  }

  /**
   * Pause/unpause playback
   */
  public setPaused(paused: boolean): void {
    this.isPaused = paused;
    if (!paused) {
      this.lastRealTime = Date.now();
    }
  }

  /**
   * Get minimum timestamp (starting point)
   */
  public getMinTimestamp(): number {
    return this.MIN_TIMESTAMP;
  }

  /**
   * Check if time is moving forward
   */
  public isMovingForward(): boolean {
    return this.playbackSpeed > 0;
  }
}

// Singleton instance
const gameTimeManagerInstance = new GameTimeManager();

/**
 * Get current game time in milliseconds
 * Returns simulated time if in replay mode, otherwise returns real time
 */
export function getGameTimeMs(): number {
  if (gameTimeManagerInstance.isReplayMode()) {
    const time = gameTimeManagerInstance.getSimulatedTimestamp();
    return time;
  }
  return Date.now();
}

/**
 * Get current game time in seconds (Unix timestamp)
 */
export function getGameTimeSeconds(): number {
  return Math.floor(getGameTimeMs() / 1000);
}

/**
 * Configure replay mode
 * @param enabled - Whether to enable replay mode
 * @param startTimestamp - Optional starting timestamp in milliseconds
 */
export function setReplayMode(enabled: boolean, startTimestamp?: number): void {
  console.log('[TimeUtils] setReplayMode called:', { enabled, startTimestamp, 
    currentTime: startTimestamp ? new Date(startTimestamp).toISOString() : 'undefined' });
  gameTimeManagerInstance.setReplayMode(enabled, startTimestamp);
  // Auto start advancing when entering replay mode to ensure consistent progression
  if (enabled && !gameTimeManagerInstance.isAutoAdvanceRunning()) {
    console.log('[TimeUtils] Auto-starting advance for replay mode');
    gameTimeManagerInstance.startAutoAdvance(100);
  }
}

/**
 * Set simulated timestamp for replay mode
 * @param timestamp - Timestamp in milliseconds
 */
export function setSimulatedTimestamp(timestamp: number): void {
  gameTimeManagerInstance.setSimulatedTimestamp(timestamp);
}

/**
 * Get timestamp for a specific block
 * @param blockNumber - Block number
 */
export async function getTimestampForBlock(
  blockNumber: number
): Promise<number> {
  return gameTimeManagerInstance.getTimestampForBlock(blockNumber);
}

/**
 * Set playback speed for auto-advance
 * @param speed - Playback speed (1.0 = real-time, 2.0 = 2x speed, 0.5 = half speed)
 */
export function setPlaybackSpeed(speed: number): void {
  gameTimeManagerInstance.setPlaybackSpeed(speed);
}

/**
 * Get current playback speed
 */
export function getPlaybackSpeed(): number {
  return gameTimeManagerInstance.getPlaybackSpeed();
}

/**
 * Start auto-advancing simulated time
 * @param intervalMs - Update interval in milliseconds (default: 100ms)
 */
export function startAutoAdvance(intervalMs: number = 100): void {
  gameTimeManagerInstance.startAutoAdvance(intervalMs);
}

/**
 * Stop auto-advancing simulated time
 */
export function stopAutoAdvance(): void {
  gameTimeManagerInstance.stopAutoAdvance();
}

/**
 * Check if auto-advance is running
 */
export function isAutoAdvanceRunning(): boolean {
  return gameTimeManagerInstance.isAutoAdvanceRunning();
}

// Export the manager for advanced usage
export { gameTimeManagerInstance as gameTimeManager };

/**
 * Get the initial timestamp (when replay mode was started)
 * @returns Initial timestamp in milliseconds, or null if not set
 */
export function getInitialTimestamp(): number | null {
  return gameTimeManagerInstance.getInitialTimestamp();
}

/**
 * Toggle playback direction (forward/reverse)
 */
export function togglePlaybackDirection(): void {
  gameTimeManagerInstance.toggleDirection();
}

/**
 * Check if time is paused
 */
export function isPaused(): boolean {
  return gameTimeManagerInstance.getIsPaused();
}

/**
 * Pause/unpause playback
 */
export function setPaused(paused: boolean): void {
  gameTimeManagerInstance.setPaused(paused);
}

/**
 * Get minimum timestamp (starting point)
 */
export function getMinTimestamp(): number {
  return gameTimeManagerInstance.getMinTimestamp();
}

/**
 * Check if time is moving forward
 */
export function isMovingForward(): boolean {
  return gameTimeManagerInstance.isMovingForward();
}

export function formatDuration(durationMs: number) {
  if (durationMs < 0) {
    return "";
  }

  const hours = Math.floor(durationMs / 1000 / 60 / 60);
  const minutes = Math.floor((durationMs - hours * 60 * 60 * 1000) / 1000 / 60);
  const seconds = Math.floor(
    (durationMs - hours * 60 * 60 * 1000 - minutes * 60 * 1000) / 1000
  );

  return (
    timestampSection(hours) +
    ":" +
    timestampSection(minutes) +
    ":" +
    timestampSection(seconds)
  );
}

function timestampSection(value: number) {
  return value.toString().padStart(2, "0");
}
